import { useState, useEffect, useCallback, useMemo } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { RootState } from "../../store";
import { TextEnum } from "../../constants/text";
import { TextBox } from "../../constants/features/input";
import { MessageModal } from "../../constants/features/modal";
import { CustomBreadCrumb } from "../../constants/features/breadcrumb";
import {
  useAppDispatch,
  useAppParams,
  useAppSelector,
} from "../../constants/global-hooks";
import "./create-push-message.scss";
import { CustomCheckbox } from "../../constants/features/checkbox";
import {
  resetPushMessage,
  setPushMessageImmediate,
  setPushMessageSendDate,
  setPushMessageSendTime,
} from "../../constants/reducers/push-message.reducer";
import { CustomDatePicker } from "../../constants/features/date-picker";
import { CustomTimePicker } from "../../constants/features/time-picker";
import {
  useHandlePushMessageAlertMessageChange,
  useHandlePushMessageBodyChange,
  useHandlePushMessageInputDeciderChange,
  useHandlePushMessageNavigateBack,
  useHandlePushMessageTitleChange,
} from "../../constants/utils/push-message-utils";
import {
  createPushMessage,
  getOnePushMessage,
} from "../../actions/push-message";

export const useHandlePushMessageSendDateChange = (dispatch: Function) => {
  return useCallback(
    (value: string) => {
      if (!value) {
        dispatch(setPushMessageSendDate(""));
        return;
      }

      if (value.includes("T")) {
        dispatch(setPushMessageSendDate(value.split("T")[0]));
        return;
      }

      const date = new Date(value);
      const formattedDate =
        `${date.getFullYear()}-` +
        `${String(date.getMonth() + 1).padStart(2, "0")}-` +
        `${String(date.getDate()).padStart(2, "0")}`;

      dispatch(setPushMessageSendDate(formattedDate));
    },
    [dispatch],
  );
};

export const useHandlePushMessageSendTimeChange = (dispatch: Function) => {
  return useCallback(
    (value: string) => {
      dispatch(setPushMessageSendTime(value));
    },
    [dispatch],
  );
};

export const CreatePushMessagePage = () => {
  const params = useAppParams();
  const dispatch = useAppDispatch();
  const {
    title,
    body,
    immediate,
    sendDate,
    sendTime,
    errorMessage,
    pushMessageActionStart,
    pushMessage,
  } = useAppSelector((state: RootState) => state.pushMessage);
  const [state, setState] = useState({
    buttonDisabler: true,
    clicked: false,
    immediateCheckBox: immediate ?? false,
  });

  const handleNavigateBack = useHandlePushMessageNavigateBack(dispatch);
  const handleAlertMessage = useHandlePushMessageAlertMessageChange(dispatch);
  const handleInputDeciderChange =
    useHandlePushMessageInputDeciderChange(dispatch);

  const getPushMessage = async (id: number) => {
    const response = await getOnePushMessage({ id });
    dispatch(
      setPushMessageSendDate(
        normalizeDate(response!.data.pushMessage.sendDate, "date"),
      ),
    );
    dispatch(
      setPushMessageSendTime(
        normalizeDate(response!.data.pushMessage.sendDate, "time"),
      ),
    );
  };

  useEffect(() => {
    dispatch(resetPushMessage());
    dispatch(setPushMessageSendDate(""));
    dispatch(setPushMessageSendTime(""));
    if (params.id) {
      getPushMessage(parseInt(params.id));
    }
  }, [dispatch, params]);

  const handleSave = async () => {
    handleAlertMessage(null);
    await createPushMessage();
    setState((prevState) => ({ ...prevState, clicked: true }));
  };

  useEffect(() => {
    if (state.clicked && errorMessage === null && !pushMessageActionStart) {
      handleNavigateBack();
    }
    setState((prevState) => ({ ...prevState, clicked: false }));
  }, [handleNavigateBack, state.clicked, errorMessage, pushMessageActionStart]);

  const handleImmediateCheckboxChange = (value: boolean) => {
    setState((prevState) => ({ ...prevState, immediateCheckBox: value }));
    dispatch(setPushMessageImmediate(value));
  };

  useEffect(() => {
    const hasRequiredFields = !!(title && body);
    const isImmediate = !!state.immediateCheckBox;
    const isScheduled = !state.immediateCheckBox && !!sendDate && !!sendTime;

    const hasConflict = isImmediate && (!!sendDate || !!sendTime);

    const isFormValid =
      hasRequiredFields && (isImmediate || isScheduled) && !hasConflict;

    setState((prevState) => ({
      ...prevState,
      buttonDisabler: !!pushMessage ? true : !isFormValid,
    }));
  }, [title, body, state.immediateCheckBox, sendDate, sendTime, pushMessage]);

  useEffect(() => {
    if (state.immediateCheckBox) {
      dispatch(setPushMessageSendDate(""));
      dispatch(setPushMessageSendTime(""));
    }
  }, [state.immediateCheckBox, sendDate, sendTime]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      immediateCheckBox: immediate ?? false,
    }));
  }, [immediate]);

  const normalizeDate = (isoString: string, type: string): string => {
    const localDate: Date = new Date(isoString);
    if (!localDate) return "";
    if (type === "date") {
      const year: number = localDate.getFullYear();
      const month: string = String(localDate.getMonth() + 1).padStart(2, "0");
      const day: string = String(localDate.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    } else {
      const hours: string = String(localDate.getHours()).padStart(2, "0");
      const minutes: string = String(localDate.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    }
  };

  const maxAllowedDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);
    return date.toISOString().split("T")[0];
  }, []);

  return (
    <main className="main-page">
      <div className="title">
        {pushMessage ? TextEnum.MODIFY : TextEnum.CREATION_MODAL_TITLE}
      </div>
      <CustomBreadCrumb
        title={TextEnum.PUSH_MESSAGE_PAGE_TITLE}
        onClick={handleNavigateBack}
        modifyTitle={TextEnum.GET_PUSH_MESSAGE_PAGE_TITLE}
        createTitle={TextEnum.CREATE_PUSH_MESSAGE_PAGE_TITLE}
        modify={pushMessage ? true : false}
      />
      <div className="container">
        <div className="inputContainer">
          <TextBox
            onChange={useHandlePushMessageTitleChange(dispatch)}
            value={title}
            type="input"
            customErrorMessage={TextEnum.NAME_REQUIRED_ERROR_MESSAGE}
            setDecider={handleInputDeciderChange}
            placeholder={TextEnum.PUSH_MESSAGE_TITLE}
            disabled={!!pushMessage}
          />
        </div>
        <div className="inputContainer">
          <TextBox
            onChange={useHandlePushMessageBodyChange(dispatch)}
            value={body}
            type="textbox"
            maxLength={255}
            customErrorMessage={TextEnum.TEXT_REQUIRED_ERROR_MESSAGE}
            setDecider={handleInputDeciderChange}
            placeholder={TextEnum.PUSH_MESSAGE_BODY}
            disabled={!!pushMessage}
          />
        </div>
        <div className="left-side-div">
          <div className="send-date-title">Kiküldés ideje</div>

          <div
            className="checkbox-container"
            style={{
              width: "fit-content",
              justifyContent: "flex-start",
              height: "fit-content",
              marginTop: "8px",
            }}
          >
            <div className="custom-checkbox" style={{ margin: "0px" }}>
              <CustomCheckbox
                onChange={handleImmediateCheckboxChange}
                label={TextEnum.PUSH_MESSAGE_IMMEDIATE_CHECKBOX_TITLE}
                checked={state.immediateCheckBox}
                disabledFrom={
                  pushMessage ? new Date().toDateString() : "2099-12-31"
                }
              />
            </div>
          </div>

          <div className="scheduled-title">
            Időzítve (legfeljebb előre 3 hónappal)
          </div>
        </div>

        <div className="inputContainer">
          <div className="range-picker-container">
            <CustomDatePicker
              validDate={sendDate}
              placeholder={
                new Date(sendDate) >= new Date(new Date().setHours(0, 0, 0, 0))
                  ? ""
                  : !sendDate
                    ? TextEnum.DATE
                    : sendDate
              }
              onChange={useHandlePushMessageSendDateChange(dispatch)}
              disabledFrom={
                pushMessage ? new Date().toDateString() : "2099-12-31"
              }
              disabledTo={maxAllowedDate}
            />
            <CustomTimePicker
              validTime={sendTime}
              placeholder={!sendTime ? TextEnum.TIME : ""}
              onChange={useHandlePushMessageSendTimeChange(dispatch)}
              isDisabled={pushMessage ? true : false}
            />
          </div>
        </div>

        <button
          disabled={pushMessageActionStart || state.buttonDisabler}
          className={`create-push-message-button ${
            state.buttonDisabler || pushMessageActionStart ? "disabled" : ""
          }`}
          onClick={handleSave}
        >
          {pushMessageActionStart ? <LoadingOutlined /> : TextEnum.SAVE_BUTTON}
        </button>
      </div>

      {errorMessage && (
        <MessageModal
          title=""
          message={errorMessage}
          onCloseClick={handleAlertMessage}
        />
      )}
    </main>
  );
};
